import { useState } from "react";
import type { ReactNode } from "react";
import { Modal } from "./Modal";
import { useT } from "@/contexts/I18nContext";

/**
 * A number, and the way to find out what is behind it.
 *
 * Restio's pattern: the tile carries the figure, and pressing it opens the
 * breakdown. The rule that makes it worth anything is that **the modal shows
 * the same numbers the tile was computed from** — a summary and a detail that
 * are calculated separately eventually disagree, and the day they do nobody can
 * tell which one is wrong.
 */
export function MetricCard({
  labelKey,
  value,
  detailKey,
  children,
}: {
  labelKey: string;
  value: string;
  detailKey: string;
  children: ReactNode;
}) {
  const t = useT();
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={`${t(labelKey)} — ${t("metrics.openDetail")}`}
        className="card p-5 text-left transition-colors hover:border-ink-faint"
      >
        <p className="text-sm text-ink-muted">{t(labelKey)}</p>
        <p className="mt-1 font-display text-3xl text-ink">{value}</p>
        <p className="mt-2 text-xs text-ink-faint">{t(detailKey)}</p>
      </button>

      <Modal open={open} onClose={() => setOpen(false)} titleKey={labelKey}>
        {children}
      </Modal>
    </>
  );
}

/** A row inside a detail modal. Two columns, one fact. */
export function DetailRow({ labelKey, value }: { labelKey: string; value: string }) {
  const t = useT();
  return (
    <div className="flex items-baseline justify-between gap-6 border-b border-paper-warm py-2 last:border-0">
      <span className="text-sm text-ink-muted">{t(labelKey)}</span>
      <span className="text-sm text-ink">{value}</span>
    </div>
  );
}
